import type { MediaRef } from "@/types";

/**
 * URLs for the backend's media routes
 * (`packages/backend/src/features/media/media.routes.ts`).
 *
 * Every one is same-origin `/api/...`, which Traefik routes to the backend in
 * deployment and a Next rewrite routes locally. They are plain strings rather
 * than a hook because most callers are `<img src>` and Mapbox's sprite loader,
 * neither of which is in React's control.
 */

/** How an image should fill the box it was asked for. */
export type MediaFit = "cover" | "contain";

/**
 * A resize spec for the image route.
 *
 * `_inside` is the backend's spelling for "fit the whole image in the box"; bare
 * `WxH` fills it and crops. Only `contain` maps to `_inside` — the other CSS
 * `object-fit` values the config allows (`fill`, `none`) are how the *element*
 * lays the image out, not how it should be resized, so they take the default.
 */
export function imageSize(
  width: number,
  height: number,
  fit: MediaFit = "cover"
): string {
  return `${width}x${height}${fit === "contain" ? "_inside" : ""}`;
}

/**
 * The part of a stored path the media routes actually take.
 *
 * `filePath` is stored as `/{projectId}/{folder}/{file}`, and the route puts the
 * project folder back itself from the resolved domain — so sending it would
 * address `/{projectId}/{projectId}/…`. Stripping it here is the mirror of that.
 *
 * The project segment is passed in rather than assumed to be the first one: a
 * path that doesn't carry it is returned as-is, so a hand-written relative path
 * still works.
 */
export function mediaPath(
  filePath: string | undefined,
  projectSegment: string | undefined
): string | null {
  if (!filePath) return null;

  const trimmed = filePath.replace(/^\/+/, "");
  if (!trimmed) return null;

  const prefix = projectSegment ? `${projectSegment}/` : null;
  return prefix && trimmed.startsWith(prefix)
    ? trimmed.slice(prefix.length)
    : trimmed;
}

/** Percent-encode each segment, leaving the separators intact. */
function encodePath(path: string): string {
  return path.split("/").map(encodeURIComponent).join("/");
}

export interface MediaContext {
  /** The tenant's domain — how the media routes address a project. */
  domain: string | undefined;
  /** The project id, which is the prefix on every stored path. */
  projectSegment: string | undefined;
}

/**
 * A resized image URL, or null when there is nothing to show.
 *
 * Null rather than a placeholder URL so callers decide what an absent image
 * means — the result list falls back to the tenant's logo, the gallery omits the
 * entry entirely.
 */
export function imageUrl(
  ref: MediaRef | string | undefined,
  size: string,
  { domain, projectSegment }: MediaContext
): string | null {
  const filePath = typeof ref === "string" ? ref : ref?.filePath;
  const path = mediaPath(filePath, projectSegment);
  if (!path || !domain) return null;

  return `/api/projects/${encodeURIComponent(domain)}/image/${size}/${encodePath(path)}`;
}

/**
 * A logo URL. Logos are addressed by file name alone, since the route knows the
 * folder they live in, and are served unresized.
 */
export function logoUrl(
  fileName: string | undefined,
  domain: string | undefined
): string | null {
  if (!fileName || !domain) return null;
  return `/api/projects/${encodeURIComponent(domain)}/logo/${encodeURIComponent(fileName)}`;
}

/** A stored file served verbatim — PDFs and other non-image attachments. */
export function assetUrl(
  ref: MediaRef | string | undefined,
  { domain, projectSegment }: MediaContext
): string | null {
  const filePath = typeof ref === "string" ? ref : ref?.filePath;
  const path = mediaPath(filePath, projectSegment);
  if (!path || !domain) return null;

  return `/api/projects/${encodeURIComponent(domain)}/asset/${encodePath(path)}`;
}

/**
 * A shared asset, addressed without a tenant.
 *
 * Basemap thumbnails are the case: the same picker is shown for every project,
 * so the stored path is used whole — project segment included — instead of being
 * resolved through a domain.
 */
export function commonUrl(ref: MediaRef | string | undefined): string | null {
  const filePath = typeof ref === "string" ? ref : ref?.filePath;
  if (!filePath) return null;

  const trimmed = filePath.replace(/^\/+/, "");
  return trimmed ? `/api/projects/common/${encodePath(trimmed)}` : null;
}
